import { useContext, useEffect, useState } from 'react';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { SiteContext } from '../../../context/SiteContext';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ButtonBack } from '../../common/Button/ButtonBack';
import {
  AddressDetails,
  AddressDetailsDropDowns,
  InsuranceJourneyContext,
  SelectOpt
} from '../../common/CommonTypes';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import RadioOptions from '../../common/RadioOptions/RadioOptions';
import {
  populateSelectOptions,
  setCountryCode
} from '../../common/HelperMethods';
import {
  InsuranceJourneyHelperText,
  InsuranceSaveForLaterMessage,
  PageAbsolutePath
} from '../../../consts/Common';
import { ButtonCommand } from '../../common/Button/ButtonCommand';
import ClickableHelpText from '../../common/HelpText/ClickableHelpText';
import AddressManager from '../../common/Address/AddressManager';
import { PagesWithDropdown } from '../../common/Constants';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import LoadingContainer from '../../common/LoadingContainer/LoadingContainer';
import { useErrorBoundary } from 'react-error-boundary';

export type PageErrors = {
  addressError: string;
  correspondenceAddressError: string;
  overnightParkingLocations: string;
  numberOfCars: string;
  isCorrespondenceAddress: string;
};

const initialErrors: PageErrors = {
  overnightParkingLocations: '',
  addressError: '',
  numberOfCars: '',
  correspondenceAddressError: '',
  isCorrespondenceAddress: ''
};

enum PageState {
  Initial,
  ValidatingAddress,
  UpdatingAddress,
  ValidatingCorrespondenceAddress,
  UpdatingCorrespondenceAddress,
  Redirecting,
  BackUpdateAddress,
  BackUpdateCorrespondenceAddress
}

const AddressDetailsPage = (): JSX.Element => {
  const { site } = useContext(SiteContext);
  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();

  const { savingQuote, addressDetails, staticData, pageLoading } = journey;
  const { addressDetailsPage, commonEvents } = pageEvents;

  const {
    onChangeOvernightParkedAddress,
    onChangeCorrespondenceAddress,
    onChangeIsThisCorrespondenceAddress,
    onChangeLocationVehicleParkedOvernight,
    onChangeNumberOfCarsAtHousehold
  } = addressDetailsPage;
  const { setSavingQuote, setStaticData } = commonEvents;

  const {
    overnightParkedAddress,
    isThisCorrespondenceAddress,
    correspondenceAddress,
    locationVehicleParkedOvernight,
    numberOfCarsAtHousehold
  } = addressDetails;

  const token = site.user.token;
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const [
    overnightParkingLocationsOptions,
    setOvernightParkingLocationsOptions
  ] = useState<SelectOpt[]>([]);
  const [numberOfCarsOptions, setNumberOfCarsOptions] = useState<SelectOpt[]>(
    []
  );

  const [errors, setErrors] = useState<PageErrors>(initialErrors);
  const [redirect, setRedirect] = useState('');
  const [pageState, setPageState] = useState(PageState.Initial);
  const [needsSave, setNeedsSave] = useState(false);

  const { getAddressDetailsPageDropdownValues } = useCarInsuranceController();

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const pageDropdownValues = staticData?.find(
      (d) => d.page === PagesWithDropdown.AddressDetailsPage
    )?.values;

    !pageDropdownValues &&
      getAddressDetailsPageDropdownValues(
        token,
        getAddressDetailsPageDropdownValuesSuccess,
        onBackendFailure
      );
  }, []);

  useEffect(() => {
    const parkingLocationOptions = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.AddressDetailsPage &&
        d.controlName === 'overnight-parking-locations'
    )?.values;

    parkingLocationOptions
      ? setOvernightParkingLocationsOptions(parkingLocationOptions)
      : [];

    const noOfCarsOptions = staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.AddressDetailsPage &&
        d.controlName === 'no-of-cars'
    )?.values;

    noOfCarsOptions ? setNumberOfCarsOptions(noOfCarsOptions) : [];
  }, [staticData]);

  useEffect(() => {
    if (pageState === PageState.UpdatingAddress) {
      if (isThisCorrespondenceAddress) {
        setPageState(PageState.Redirecting);
      } else {
        setPageState(PageState.ValidatingCorrespondenceAddress);
      }
    } else if (pageState === PageState.BackUpdateAddress) {
      if (isThisCorrespondenceAddress === false) {
        setPageState(PageState.BackUpdateCorrespondenceAddress);
      } else {
        if (needsSave) {
          setSavingQuote(true);
          setNeedsSave(false);
          setPageState(PageState.Initial);
        } else {
          setPageState(PageState.Redirecting);
        }
      }
    }
  }, [overnightParkedAddress]);

  useEffect(() => {
    if (pageState === PageState.UpdatingCorrespondenceAddress) {
      setPageState(PageState.Redirecting);
    } else if (pageState === PageState.BackUpdateCorrespondenceAddress) {
      if (needsSave) {
        setSavingQuote(true);
        setNeedsSave(false);
        setPageState(PageState.Initial);
      } else {
        setPageState(PageState.Redirecting);
      }
    }
  }, [correspondenceAddress]);

  useEffect(() => {
    if (redirect && pageState === PageState.Redirecting) {
      navigate(redirect);
    }
  }, [redirect, pageState]);

  const getAddressDetailsPageDropdownValuesSuccess = (
    dropdownValues: AddressDetailsDropDowns
  ) => {
    const newValues = [
      {
        page: PagesWithDropdown.AddressDetailsPage,
        controlName: 'overnight-parking-locations',
        values: populateSelectOptions(dropdownValues.overnightParkingLocations)
      },
      {
        page: PagesWithDropdown.AddressDetailsPage,
        controlName: 'no-of-cars',
        values: populateSelectOptions(dropdownValues.numberOfCars)
      }
    ];

    setStaticData(staticData ? staticData.concat(...newValues) : newValues);
  };

  const validate = (): boolean => {
    let isValid = true;
    const error: PageErrors = {
      ...initialErrors
    };

    if (
      SelectInputHelper.isNullValue(locationVehicleParkedOvernight) ||
      SelectInputHelper.isEmptyValue(locationVehicleParkedOvernight)
    ) {
      error.overnightParkingLocations =
        'Overnight parking locations is required';
      isValid = false;
    }

    if (SelectInputHelper.isNullValue(numberOfCarsAtHousehold)) {
      error.numberOfCars = 'Number of cars is required';
      isValid = false;
    }

    if (
      isThisCorrespondenceAddress === undefined ||
      isThisCorrespondenceAddress === null
    ) {
      error.isCorrespondenceAddress = 'This field is required';
      isValid = false;
    }

    setErrors(error);

    if (isValid) {
      setPageState(PageState.ValidatingAddress);
    } else {
      setPageState(PageState.Initial);
    }

    return isValid;
  };

  const onNextClick = () => {
    if (validate()) {
      setRedirect(
        setCountryCode(
          PageAbsolutePath.Insurance.AdditionalDriverDetailsPage,
          site.countryCode
        )
      );
    }
  };

  const onBackClick = () => {
    setPageState(PageState.BackUpdateAddress);
    setRedirect(
      setCountryCode(
        PageAbsolutePath.Insurance.InsuranceCoverDetailsPage,
        site.countryCode
      )
    );
  };

  const handleOnClickSaveForLater = (): void => {
    setPageState(PageState.BackUpdateAddress);
    setNeedsSave(true);
  };

  const handleAddressUpdate = (address: AddressDetails) => {
    onChangeOvernightParkedAddress(address);
  };

  const handleCorrespondenceAddressUpdate = (address: AddressDetails) => {
    onChangeCorrespondenceAddress(address);
  };

  const handleAddressValidate = (isValid: boolean) => {
    if (isValid) {
      setPageState(PageState.UpdatingAddress);
    } else {
      setPageState(PageState.Initial);
    }
  };

  const handleCorrespondenceAddressValidate = (isValid: boolean) => {
    if (isValid) {
      setPageState(PageState.UpdatingCorrespondenceAddress);
    } else {
      setPageState(PageState.Initial);
    }
  };

  return (
    <LoadingContainer
      loading={pageLoading || savingQuote}
      message={InsuranceSaveForLaterMessage}>
      <>
        <div className='one-col'>
          <ButtonCommand
            name='btn-back-top'
            text={'< Back'}
            onClick={onBackClick}
          />
        </div>
        <h4 className='section-header m1-top'>Address Details</h4>
        <div>
          <div className='control-label help-text'>
            {
              'What is the postcode where the car will usually be parked overnight?'
            }
            <ClickableHelpText
              helpText={
                InsuranceJourneyHelperText.Pages.AddressDetails
                  .PostcodeOvernightParked
              }
            />
          </div>
          <AddressManager
            findAddressTitle='Postcode'
            initialAddress={overnightParkedAddress}
            update={
              pageState === PageState.UpdatingAddress ||
              pageState === PageState.BackUpdateAddress
            }
            validate={pageState === PageState.ValidatingAddress}
            token={site.user.token}
            onAddressUpdate={handleAddressUpdate}
            onAddressValidated={handleAddressValidate}
            allowManualEntry
          />
        </div>

        <div className='one-col'>
          <div>
            <div className='max-content'>
              <RadioOptions
                errorMessage={errors.isCorrespondenceAddress}
                selectedOption={
                  isThisCorrespondenceAddress === null
                    ? ''
                    : isThisCorrespondenceAddress
                      ? 'Yes'
                      : 'No'
                }
                optionsList={['Yes', 'No']}
                setSelectedOption={(value) =>
                  onChangeIsThisCorrespondenceAddress(value === 'Yes')
                }
                label={'Is this the correspondence address?'}
                name='isThisCorrespondenceAddress'
              />
            </div>
          </div>
        </div>

        {isThisCorrespondenceAddress === false && (
          <>
            <AddressManager
              findAddressTitle='Correspondence Address'
              initialAddress={correspondenceAddress}
              update={
                pageState === PageState.UpdatingCorrespondenceAddress ||
                pageState === PageState.BackUpdateCorrespondenceAddress
              }
              validate={pageState === PageState.ValidatingCorrespondenceAddress}
              token={site.user.token}
              onAddressUpdate={handleCorrespondenceAddressUpdate}
              onAddressValidated={handleCorrespondenceAddressValidate}
              allowManualEntry
            />
          </>
        )}
        <div className='one-col'>
          <div className='control-label help-text'>
            Where will the vehicle be parked overnight?
            <ClickableHelpText
              helpText={
                InsuranceJourneyHelperText.Pages.AddressDetails.OvernightParked
              }
            />
          </div>
          <div className='control'>
            <SelectInput
              name='parkinglocations'
              className='input-text w250'
              value={locationVehicleParkedOvernight}
              onChange={onChangeLocationVehicleParkedOvernight}
              options={overnightParkingLocationsOptions}
              forceDefault
              addDefaultOption
              error={errors.overnightParkingLocations}
            />
          </div>
        </div>

        <div className='one-col'>
          <div className='control-label help-text'>
            How many cars are kept at your household (excluding this one)?
            <ClickableHelpText
              helpText={
                InsuranceJourneyHelperText.Pages.AddressDetails.CarsInHousehold
              }
            />
          </div>
          <div className='control'>
            <SelectInput
              name='numberofcars'
              className='input-text w250'
              value={numberOfCarsAtHousehold}
              onChange={onChangeNumberOfCarsAtHousehold}
              options={numberOfCarsOptions}
              forceDefault
              addDefaultOption
              error={errors.numberOfCars}
            />
          </div>
        </div>

        <div className='flex-row form-nav'>
          <ButtonBack
            name='btn-back'
            text='Back'
            onClick={onBackClick}
            additionalClassNames='btn-back'
          />
          <ButtonContinue
            name='btn-save-later'
            text='Save For Later'
            onClick={handleOnClickSaveForLater}
            additionalClassNames='btn-save'
          />
          <ButtonContinue
            name='btn-next'
            text='Next'
            onClick={onNextClick}
            additionalClassNames='btn-continue'
          />
        </div>
      </>
    </LoadingContainer>
  );
};

export default AddressDetailsPage;
